import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { showSubframeToast } from "../../../components/common/CasePrepToast";
import { CasePrepUscisFormsAPI } from "../api";
import type {
  CasePrepUscisFilledForm,
  CasePrepUscisFormVersion
} from "../types";
import { FormVersionHistoryDrawer } from "./FormVersionHistoryDrawer";

const VERSIONS_PAGE_SIZE = 10;

const showVersionToast = (params: {
  variant: "brand" | "neutral" | "error" | "success";
  title: string;
  description: string;
  /** Feather icon name, e.g. "FeatherClock". */
  icon?: string;
  durationMs?: number;
}) => {
  const { durationMs = 3500, ...rest } = params;
  showSubframeToast({ ...rest, durationMs });
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  individualId: string | null;
  filledFormId: string | null;
  formTitle: string;
  /** Called after a successful restore with the restored form. */
  onRestored?: (restored: CasePrepUscisFilledForm) => void;
};

/**
 * Self-contained version history experience (drawer + restore confirm dialog).
 * Owns version fetching/pagination and the restore flow given a filled-form id.
 */
export const FormVersionHistory = ({
  open,
  onOpenChange,
  individualId,
  filledFormId,
  formTitle,
  onRestored
}: Props) => {
  const [versions, setVersions] = useState<CasePrepUscisFormVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingMoreVersions, setIsLoadingMoreVersions] = useState(false);
  const [hasMoreVersions, setHasMoreVersions] = useState(false);
  const [versioningError, setVersioningError] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [pendingRestoreVersion, setPendingRestoreVersion] =
    useState<CasePrepUscisFormVersion | null>(null);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);

  const versionsCountRef = useRef(0);
  const isLoadingVersionsRef = useRef(false);
  const isLoadingMoreVersionsRef = useRef(false);

  useEffect(() => {
    versionsCountRef.current = versions.length;
  }, [versions.length]);

  const fetchVersions = useCallback(
    async (opts?: { append?: boolean }) => {
      if (!individualId || !filledFormId) return;
      const append = Boolean(opts?.append);

      if (append) {
        if (isLoadingMoreVersionsRef.current || isLoadingVersionsRef.current)
          return;
        isLoadingMoreVersionsRef.current = true;
        setIsLoadingMoreVersions(true);
      } else {
        isLoadingVersionsRef.current = true;
        setIsLoadingVersions(true);
        setVersioningError(null);
        setHasMoreVersions(false);
      }

      try {
        const offset = append ? versionsCountRef.current : 0;
        const raw = await CasePrepUscisFormsAPI.listFilledFormVersions(
          individualId,
          filledFormId,
          { limit: VERSIONS_PAGE_SIZE, offset }
        );

        setHasMoreVersions(raw.length === VERSIONS_PAGE_SIZE);
        setVersions((prev) => {
          if (!append) return raw;

          const seen = new Set(prev.map((v) => v.version_id));
          const next = [...prev];
          raw.forEach((v) => {
            if (v.version_id && !seen.has(v.version_id)) {
              seen.add(v.version_id);
              next.push(v);
            }
          });
          return next;
        });
      } catch (e) {
        Sentry.captureException(e);
        if (!append) {
          setVersioningError("Failed to load version history.");
        } else {
          showVersionToast({
            variant: "error",
            title: "Couldn't load more versions",
            description: "Please try again.",
            durationMs: 4000
          });
        }
      } finally {
        if (append) {
          isLoadingMoreVersionsRef.current = false;
          setIsLoadingMoreVersions(false);
        } else {
          isLoadingVersionsRef.current = false;
          setIsLoadingVersions(false);
        }
      }
    },
    [individualId, filledFormId]
  );

  useEffect(() => {
    if (!open) return;
    fetchVersions().catch(() => {});
  }, [open, fetchVersions]);

  const handleRestoreClick = useCallback(
    (version: CasePrepUscisFormVersion) => {
      setPendingRestoreVersion(version);
      setIsRestoreConfirmOpen(true);
    },
    []
  );

  const performRestore = useCallback(async () => {
    if (!individualId || !filledFormId || !pendingRestoreVersion?.version_id)
      return;

    const versionId = pendingRestoreVersion.version_id;
    setRestoringVersionId(versionId);
    try {
      const restored = await CasePrepUscisFormsAPI.restoreFilledFormVersion(
        individualId,
        filledFormId,
        versionId
      );

      showVersionToast({
        variant: "brand",
        icon: "FeatherClock",
        title: "Version restored",
        description: "This form has been restored to the selected version.",
        durationMs: 4000
      });

      setIsRestoreConfirmOpen(false);
      setPendingRestoreVersion(null);

      await fetchVersions();
      onOpenChange(false);
      onRestored?.(restored);
    } catch (e) {
      Sentry.captureException(e);
      showVersionToast({
        variant: "error",
        icon: "FeatherAlertCircle",
        title: "Restore failed",
        description: "We couldn't restore that version. Please try again.",
        durationMs: 5000
      });
    } finally {
      setRestoringVersionId(null);
    }
  }, [
    individualId,
    filledFormId,
    fetchVersions,
    onOpenChange,
    onRestored,
    pendingRestoreVersion
  ]);

  return (
    <>
      <FormVersionHistoryDrawer
        open={open}
        onOpenChange={onOpenChange}
        individualId={individualId}
        filledFormId={filledFormId}
        formTitle={formTitle}
        versions={versions}
        isLoading={isLoadingVersions}
        isLoadingMore={isLoadingMoreVersions}
        hasMore={hasMoreVersions}
        onLoadMore={() => {
          fetchVersions({ append: true }).catch(() => {});
        }}
        error={versioningError}
        restoringVersionId={restoringVersionId}
        onRestoreClick={handleRestoreClick}
      />

      <AlertDialog
        open={isRestoreConfirmOpen}
        onOpenChange={(next) => {
          if (!next) setIsRestoreConfirmOpen(false);
        }}
      >
        <AlertDialogContent
          className="z-2147483647 w-105 max-w-[90vw] data-[size=default]:max-w-[90vw] data-[size=default]:sm:max-w-[90vw]"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Restore This Version?</AlertDialogTitle>
            <AlertDialogDescription>
              Restoring will replace the current form answers and PDF. You can
              restore another version later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="secondary"
              disabled={Boolean(restoringVersionId)}
              onClick={() => setIsRestoreConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={Boolean(restoringVersionId)}
              onClick={() => {
                performRestore().catch(() => {});
              }}
            >
              {restoringVersionId ? <Spinner className="size-3.5" /> : null}
              Restore
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FormVersionHistory;
